import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import { Loader2, AlertCircle, Bot, FileText, RefreshCw, Clock, User, Building2, Sparkles, CheckCircle, Lightbulb, Send, Trash2, BookOpen, Search, ExternalLink, Copy, PenLine, Crown, Users, Info, ThumbsUp, ThumbsDown, ArrowUpCircle, Maximize2, Wand2, Wrench, Moon, Sun, Play, Square, Paperclip, Download, Timer, AlertTriangle, Image, Shield, Monitor, FilePlus, UserPlus, X, Mic, Bell } from 'lucide-react'
import type { MetricScore, SkillsMetricScore, MultiUserEventMetricScore, ResponseQualityMetricScore, ResponseTone, ResponseLength, SLAInfo, Attachment, EntitlementResponse } from '@/services/api'
import { performAction, fetchSLAs, fetchAttachments, logTimeEntry, fetchEntitlements, createDocumentationTicket, createSalesTicket } from '@/services/api'
import { AlertsTab } from '@/components/AlertsTab'
import { useDarkMode } from '@/hooks/useDarkMode'
import { getPriorityColor } from '@/lib/utils'
import { useLiteContext } from '@/hooks/useLiteContext'
import { useAnalysis } from '@/hooks/useAnalysis'
import { useChat } from '@/hooks/useChat'
import { useActions } from '@/hooks/useActions'
import { useResponseGenerator } from '@/hooks/useResponseGenerator'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { RecordingPanel } from '@/components/RecordingPanel'

// Metric configuration - 4x3 grid layout (12 metrics)
type MetricKey = 'clarity' | 'sentiment' | 'empathy' | 'acumen' | 'cadence' | 'disruption' | 'age' | 'priority' | 'chronic' | 'skills' | 'multiUserEvent' | 'responseQuality'

interface MetricConfig {
  key: MetricKey
  label: string
  invertColor?: boolean // true if high score is bad
}

const METRICS: MetricConfig[] = [
  { key: 'clarity', label: 'CLARITY' },
  { key: 'sentiment', label: 'SENTIMENT' },
  { key: 'empathy', label: 'EMPATHY' },
  { key: 'acumen', label: 'ACUMEN', invertColor: true },
  { key: 'cadence', label: 'CADENCE' },
  { key: 'disruption', label: 'DISRUPTION', invertColor: true },
  { key: 'age', label: 'AGE', invertColor: true },
  { key: 'priority', label: 'PRIORITY', invertColor: true },
  { key: 'chronic', label: 'CHRONIC', invertColor: true },
  { key: 'skills', label: 'SKILLS', invertColor: true },
  { key: 'multiUserEvent', label: 'MULTI-USER' },
  { key: 'responseQuality', label: 'RESPONSE' },
]

function getScoreColor(score: number, invert: boolean = false, isDark: boolean = false): string {
  const effectiveScore = invert ? 11 - score : score
  if (isDark) {
    // Rich, vibrant colors for dark theme with gradient-like appearance
    if (effectiveScore >= 7) return 'bg-gradient-to-br from-emerald-800/80 to-emerald-900/90 border-emerald-400/60 shadow-emerald-500/20 shadow-sm'
    if (effectiveScore >= 4) return 'bg-gradient-to-br from-amber-800/80 to-amber-900/90 border-amber-400/60 shadow-amber-500/20 shadow-sm'
    return 'bg-gradient-to-br from-red-800/80 to-red-900/90 border-red-400/60 shadow-red-500/20 shadow-sm'
  }
  if (effectiveScore >= 7) return 'bg-emerald-50 border-emerald-300'
  if (effectiveScore >= 4) return 'bg-amber-50 border-amber-300'
  return 'bg-red-50 border-red-300'
}

function getScoreTextColor(score: number, invert: boolean = false, isDark: boolean = false): string {
  const effectiveScore = invert ? 11 - score : score
  if (isDark) {
    // Bright, high-contrast text for dark theme
    if (effectiveScore >= 7) return 'text-emerald-300'
    if (effectiveScore >= 4) return 'text-amber-300'
    return 'text-red-300'
  }
  if (effectiveScore >= 7) return 'text-emerald-600'
  if (effectiveScore >= 4) return 'text-amber-600'
  return 'text-red-600'
}

function getEscalationColor(score: number): string {
  if (score >= 7) return 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700'
  if (score >= 4) return 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700'
  return 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700'
}

// Rotating thinking messages for AI loading states
const THINKING_MESSAGES = [
  'Analyzing the details...',
  'Searching backend database...',
  'Hang on a moment more...',
  'Parsing the data...',
  'Consulting the knowledge base...',
  'This will be worth the wait...',
  'Reviewing ticket history...',
  'Almost there...',
  'Processing your request...',
  'Gathering insights...',
]

export function LitePanel() {
  const [activeTab, setActiveTab] = useState('summary')
  const [chatInput, setChatInput] = useState('')
  const [noteInput, setNoteInput] = useState('')
  const [noteType, setNoteType] = useState<'work_note' | 'comment'>('work_note')
  const [selectedMetric, setSelectedMetric] = useState<MetricKey | null>(null)
  const [alertShown, setAlertShown] = useState(false)
  const [responseTone, setResponseTone] = useState<ResponseTone>('professional')
  const [responseLength, setResponseLength] = useState<ResponseLength>('concise')
  const [copySuccess, setCopySuccess] = useState(false)
  const [slas, setSlas] = useState<SLAInfo[]>([])
  const [slaLoading, setSlaLoading] = useState(false)
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [attachmentsLoading, setAttachmentsLoading] = useState(false)
  const [entitlements, setEntitlements] = useState<EntitlementResponse | null>(null)
  const [entitlementsLoading, setEntitlementsLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  // Time Entry state
  const [timeHours, setTimeHours] = useState(0)
  const [timeMinutes, setTimeMinutes] = useState(0)
  const [timeNotes, setTimeNotes] = useState('')
  const [timeBillable, setTimeBillable] = useState(true)
  const [timeLoading, setTimeLoading] = useState(false)
  const [timeSuccess, setTimeSuccess] = useState(false)
  const [timeError, setTimeError] = useState<string | null>(null)
  const [suggestedTime, setSuggestedTime] = useState(0)
  const [thinkingMessageIndex, setThinkingMessageIndex] = useState(0)
  // Documentation ticket state
  const [docLoading, setDocLoading] = useState(false)
  const [docSuccess, setDocSuccess] = useState<string | null>(null)
  const [docItGlueUrl, setDocItGlueUrl] = useState<string | null>(null)
  const [showEscalationToast, setShowEscalationToast] = useState(false)
  const [showDocModal, setShowDocModal] = useState(false)
  const [docTitle, setDocTitle] = useState('')
  const [docDescription, setDocDescription] = useState('')
  const chatEndRef = useRef<HTMLDivElement>(null)
  const sessionStartRef = useRef<number>(Date.now())
  // Sync dark mode from URL param (for popups opened from cross-origin iframes)
  const darkUrlParam = new URLSearchParams(window.location.search).get('dark')
  if (darkUrlParam !== null) {
    localStorage.setItem('snpod-dark-mode', darkUrlParam === '1' ? 'true' : 'false')
  }
  const { isDark, toggle: toggleDarkMode } = useDarkMode()
  const { context, loading, error, refetch } = useLiteContext()
  const { analysis, loading: analysisLoading, error: analysisError, analyze } = useAnalysis()
  const { messages, loading: chatLoading, error: chatError, sendMessage, clearChat } = useChat(context?.sys_id)
  const {
    similarRecords, similarLoading, similarError, getSimilar,
    noteLoading, noteError, noteSuccess, submitNote, resetNoteStatus,
    commentLoading, commentError, commentSuccess, submitComment, resetCommentStatus
  } = useActions()
  const {
    generatedResponse, loading: responseLoading, error: responseError,
    generate: generateCustomerResponse, clear: clearGeneratedResponse
  } = useResponseGenerator()

  // Auto-analyze when context loads (runs once when context becomes available)
  useEffect(() => {
    if (context && !analysis && !analysisLoading && !analysisError) {
      console.log('[LitePanel] Auto-triggering analysis for', context.number)
      analyze(context)
    }
  }, [context, analysis, analysisLoading, analysisError, analyze])

  // Compute alert count for the tab badge
  const alertCount = useMemo(() => {
    if (!analysis) return 0
    let count = 0
    const multiUserData = analysis.sentiment?.metrics?.multiUserEvent
    const vipData = analysis.sentiment?.metrics?.vip
    const escalationData = analysis.sentiment?.escalation
    const chronicData = analysis.sentiment?.metrics?.chronic

    if (escalationData?.recommended || (escalationData?.score && escalationData.score >= 7)) count++
    if (chronicData && chronicData.score >= 6) count++
    if (multiUserData?.isMultiUser || (multiUserData?.score && multiUserData.score >= 5)) count++
    if (vipData?.isVip) count++
    if (analysis.salesOpportunity?.detected && analysis.salesOpportunity.confidence >= 6) count++

    return count
  }, [analysis])

  // Handler to create a sales opportunity ticket
  const handleCreateSalesTicket = useCallback(async () => {
    if (!context || !analysis?.salesOpportunity) throw new Error('No context or sales opportunity data')
    const salesOpp = analysis.salesOpportunity
    const result = await createSalesTicket({
      source_sys_id: context.sys_id,
      source_number: context.number,
      source_table: context.table,
      company_sys_id: context.company?.sys_id || undefined,
      company_name: context.company?.name || undefined,
      contact_name: context.caller?.name || undefined,
      opportunity_summary: salesOpp.summary,
      opportunity_type: salesOpp.type,
      signals: salesOpp.signals,
      assignment_group_sys_id: context.assignment_group?.sys_id || undefined,
    })
    return {
      sys_id: result.ticket.sys_id,
      number: result.ticket.number,
      url: result.ticket.url,
      summary: `Sales Opportunity: ${context.company?.name || 'Unknown'} - ${salesOpp.type}`,
    }
  }, [context, analysis])

  const handleAnalyze = () => {
    if (context) {
      analyze(context)
    }
  }

  const handleSendMessage = () => {
    if (context && chatInput.trim()) {
      sendMessage(context, chatInput)
      setChatInput('')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Auto-scroll to bottom when new messages arrive (only when chat tab is active)
  useEffect(() => {
    if (activeTab === 'chat' && messages.length > 0) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [messages, activeTab])

  // Rotate thinking messages when loading
  useEffect(() => {
    const isLoading = chatLoading || analysisLoading
    if (!isLoading) {
      setThinkingMessageIndex(0)
      return
    }

    const interval = setInterval(() => {
      setThinkingMessageIndex(prev => (prev + 1) % THINKING_MESSAGES.length)
    }, 2500) // Rotate every 2.5 seconds

    return () => clearInterval(interval)
  }, [chatLoading, analysisLoading])

  // Calculate suggested time based on session duration (updates when switching to actions tab)
  useEffect(() => {
    if (activeTab === 'actions') {
      const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 60000) // minutes
      // Round up to nearest 15 minutes, minimum 15
      const suggested = Math.max(15, Math.ceil(elapsed / 15) * 15)
      setSuggestedTime(Math.min(suggested, 120)) // Cap at 2 hours
    }
  }, [activeTab])

  // Screen pop alerts for VIP, Multi-User, and Escalation
  useEffect(() => {
    if (!analysis || alertShown || analysisLoading) return

    const vipData = analysis.sentiment?.metrics?.vip
    const escalationData = analysis.sentiment?.escalation
    const multiUserData = analysis.sentiment?.metrics?.multiUserEvent

    const isVip = vipData?.isVip || false
    const needsEscalation = escalationData?.recommended || (escalationData?.score && escalationData.score >= 7)
    const isMultiUserEvent = multiUserData?.isMultiUser || (multiUserData?.score && multiUserData.score >= 5)

    if (!isVip && !needsEscalation && !isMultiUserEvent) return

    setAlertShown(true)
    console.log('[Alert] Showing popup:', { isVip, needsEscalation, isMultiUserEvent })

    // Build sections for the popup
    const sections: string[] = []

    // Multi-User Event Section (Purple)
    if (isMultiUserEvent) {
      sections.push(`
        <div class="section multiuser">
          <h2>👥 Multi-User Event Detected</h2>
          <p><strong>Scope:</strong> ${multiUserData?.affectedScope || 'Multiple users affected'}</p>
          <p>${multiUserData?.explanation || 'This issue may be affecting multiple users.'}</p>
        </div>
      `)
    }

    // VIP Section (Yellow/Gold)
    if (isVip) {
      sections.push(`
        <div class="section vip">
          <h2>👑 VIP / Executive Contact</h2>
          <p>${vipData?.reason || 'This contact requires priority handling.'}</p>
        </div>
      `)
    }

    // Escalation Section (Red)
    if (needsEscalation) {
      const factors = escalationData?.factors?.slice(0, 3) || []
      sections.push(`
        <div class="section escalation">
          <h2>🚨 Escalation Recommended</h2>
          <p>This ticket may require immediate escalation:</p>
          ${factors.length > 0 ? `<ul>${factors.map(f => `<li>${f}</li>`).join('')}</ul>` : ''}
        </div>
      `)
    }

    const popupHeight = 180 + (sections.length * 100)

    const alertWindow = window.open(
      '',
      'TicketAlert',
      `width=400,height=${popupHeight},menubar=no,toolbar=no,location=no,status=no,scrollbars=yes`
    )

    if (alertWindow) {
      alertWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Ticket Alert</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              padding: 16px;
              margin: 0;
              background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            }
            .header {
              text-align: center;
              margin-bottom: 16px;
            }
            .header h1 {
              color: #1e293b;
              font-size: 16px;
              margin: 0;
            }
            .section {
              background: white;
              border-radius: 10px;
              padding: 14px;
              margin-bottom: 12px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.08);
            }
            .section h2 {
              font-size: 14px;
              margin: 0 0 8px 0;
            }
            .section p {
              font-size: 12px;
              color: #475569;
              margin: 0 0 8px 0;
              line-height: 1.4;
            }
            .section ul {
              margin: 0;
              padding-left: 18px;
            }
            .section li {
              font-size: 11px;
              color: #475569;
              margin-bottom: 4px;
            }
            .multiuser {
              border-left: 4px solid #7c3aed;
              background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
            }
            .multiuser h2 { color: #6d28d9; }
            .vip {
              border-left: 4px solid #eab308;
              background: linear-gradient(135deg, #fefce8 0%, #fef9c3 100%);
            }
            .vip h2 { color: #a16207; }
            .escalation {
              border-left: 4px solid #dc2626;
              background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
            }
            .escalation h2 { color: #dc2626; }
            button {
              width: 100%;
              padding: 12px;
              background: #16a34a;
              color: white;
              border: none;
              border-radius: 8px;
              font-size: 13px;
              font-weight: 600;
              cursor: pointer;
            }
            button:hover {
              background: #15803d;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>⚡ Ticket Requires Attention</h1>
          </div>
          ${sections.join('')}
          <button onclick="window.close()">I Understand - Close</button>
        </body>
        </html>
      `)
      alertWindow.document.close()
    }
  }, [analysis, alertShown, analysisLoading])

  // Fetch SLAs, Attachments, and Entitlements when context loads
  useEffect(() => {
    if (!context) return

    const loadSLAsAndAttachments = async () => {
      // Fetch SLAs
      setSlaLoading(true)
      try {
        const slaResponse = await fetchSLAs(context.sys_id, context.table)
        setSlas(slaResponse.slas)
      } catch (err: any) {
        console.error('[SLA] Error:', err.message)
        setSlas([])
      } finally {
        setSlaLoading(false)
      }

      // Fetch Attachments
      setAttachmentsLoading(true)
      try {
        const attResponse = await fetchAttachments(context.sys_id, context.table)
        setAttachments(attResponse.attachments)
      } catch (err: any) {
        console.error('[Attachments] Error:', err.message)
        setAttachments([])
      } finally {
        setAttachmentsLoading(false)
      }

      // Fetch Entitlements (if company/account is available)
      if (context.company?.sys_id) {
        setEntitlementsLoading(true)
        try {
          const entResponse = await fetchEntitlements(context.company.sys_id)
          setEntitlements(entResponse)
        } catch (err: any) {
          console.error('[Entitlements] Error:', err.message)
          setEntitlements(null)
        } finally {
          setEntitlementsLoading(false)
        }
      }
    }

    loadSLAsAndAttachments()
  }, [context])

  const handleGetSimilar = () => {
    if (context) {
      getSimilar(context)
    }
  }

  const handleGenerateResponse = () => {
    if (context) {
      generateCustomerResponse(context, responseTone, responseLength)
    }
  }

  const handleCopyResponse = async () => {
    if (generatedResponse) {
      try {
        await navigator.clipboard.writeText(generatedResponse)
        setCopySuccess(true)
        setTimeout(() => setCopySuccess(false), 2000)
      } catch {
        console.error('Failed to copy to clipboard')
      }
    }
  }

  const handlePopOut = () => {
    const params = new URLSearchParams(window.location.search)
    const sysId = params.get('sys_id')
    const table = params.get('table') || 'incident'
    const userId = params.get('user_id') || params.get('sysparm_user_id') || ''
    const userName = params.get('user_name') || params.get('sysparm_user_name') || ''

    const darkParam = document.documentElement.classList.contains('dark') ? '&dark=1' : ''
    const userParams = userId ? `&user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}` : ''
    const apiKeyParam = params.get('api_key') ? `&api_key=${encodeURIComponent(params.get('api_key')!)}` : ''
    const popoutUrl = `${window.location.origin}/lite?sys_id=${sysId}&table=${table}&popout=true${darkParam}${userParams}${apiKeyParam}`
    window.open(
      popoutUrl,
      'SNPodPopout',
      'width=450,height=700,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes'
    )
  }

  const handleSecondScreen = () => {
    const params = new URLSearchParams(window.location.search)
    const sysId = params.get('sys_id')
    const table = params.get('table') || 'incident'
    const userId = params.get('user_id') || params.get('sysparm_user_id') || ''
    const userName = params.get('user_name') || params.get('sysparm_user_name') || ''

    const darkParam2 = document.documentElement.classList.contains('dark') ? '&dark=1' : ''
    const userParams2 = userId ? `&user_id=${encodeURIComponent(userId)}&user_name=${encodeURIComponent(userName)}` : ''
    const apiKeyParam2 = params.get('api_key') ? `&api_key=${encodeURIComponent(params.get('api_key')!)}` : ''
    const secondScreenUrl = `${window.location.origin}/second-screen?sys_id=${sysId}&table=${table}${darkParam2}${userParams2}${apiKeyParam2}`
    window.open(
      secondScreenUrl,
      'SNPodSecondScreen',
      'width=1400,height=800,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes'
    )
  }

  const handleQuickAction = async (action: 'resolve' | 'close' | 'reopen') => {
    if (!context) return

    setActionLoading(action)
    try {
      await performAction({
        action,
        sys_id: context.sys_id,
        table: context.table,
        close_notes: action === 'resolve' || action === 'close'
          ? 'Resolved via SNPod AI Assistant'
          : undefined,
        close_code: action === 'resolve' || action === 'close'
          ? 'Solution provided'
          : undefined,
      })
      // Refresh to show updated state
      refetch()
    } catch (err: any) {
      console.error(`[Action] ${action} failed:`, err.message)
      alert(`Action failed: ${err.message}`)
    } finally {
      setActionLoading(null)
    }
  }

  const handleOpenDocModal = () => {
    if (!context) return
    // Pre-populate the modal with default values
    setDocTitle(`Documentation needed: ${context.short_description}`)
    setDocDescription(`Documentation request created from ${context.number}.\n\nOriginal Issue: ${context.short_description}\n\nDescription: ${context.description || 'No description provided'}\n\nPlease review and create appropriate documentation for this issue type.`)
    setShowDocModal(true)
  }

  const handleSubmitDocumentation = async () => {
    if (!context) return

    setDocLoading(true)
    setDocSuccess(null)
    setDocItGlueUrl(null)
    try {
      const result = await createDocumentationTicket({
        source_sys_id: context.sys_id,
        source_number: context.number,
        source_table: context.table,
        short_description: docTitle,
        description: docDescription,
        category: 'documentation',
        company_sys_id: context.company?.sys_id || undefined,
        company_name: context.company?.name || undefined,
        assignment_group_sys_id: context.assignment_group?.sys_id || undefined,
        chat_history: messages.length > 0 ? messages.map(m => ({ role: m.role, content: m.content })) : undefined,
      })
      setDocSuccess(result.ticket.number)
      if (result.itglue_doc?.doc_url) {
        setDocItGlueUrl(result.itglue_doc.doc_url)
      }
      setShowDocModal(false)
      // Auto-hide success after 8 seconds (longer to give time to click IT Glue link)
      setTimeout(() => { setDocSuccess(null); setDocItGlueUrl(null) }, 8000)
    } catch (err: any) {
      console.error('[Documentation] Failed to create ticket:', err.message)
      alert(`Failed to create documentation ticket: ${err.message}`)
    } finally {
      setDocLoading(false)
    }
  }

  const handleEscalation = () => {
    setShowEscalationToast(true)
    setTimeout(() => setShowEscalationToast(false), 3000)
  }

  const handleSubmitNote = async () => {
    if (!context || !noteInput.trim()) return

    const success = noteType === 'work_note'
      ? await submitNote(context.sys_id, context.table, noteInput)
      : await submitComment(context.sys_id, context.table, noteInput)

    if (success) {
      setNoteInput('')
      // Auto-hide after success
      setTimeout(() => {
        resetNoteStatus()
        resetCommentStatus()
      }, 3000)
    }
  }

  const handleSubmitTimeEntry = async () => {
    if (!context) return
    if (timeHours === 0 && timeMinutes === 0) {
      setTimeError('Please enter time')
      return
    }

    setTimeLoading(true)
    setTimeError(null)
    setTimeSuccess(false)

    try {
      await logTimeEntry({
        sys_id: context.sys_id,
        table: context.table,
        hours: timeHours,
        minutes: timeMinutes,
        notes: timeNotes || undefined,
        billable: timeBillable,
      })
      setTimeSuccess(true)
      setTimeHours(0)
      setTimeMinutes(0)
      setTimeNotes('')
      // Auto-hide success after 3 seconds
      setTimeout(() => setTimeSuccess(false), 3000)
    } catch (err: any) {
      setTimeError(err.message || 'Failed to log time')
    } finally {
      setTimeLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-rezolve-green mb-3" />
        <p className="text-sm text-muted-foreground">Loading record...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <AlertCircle className="h-8 w-8 text-destructive mb-3" />
        <p className="text-sm text-destructive font-medium">{error}</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-4"
          onClick={() => refetch()}
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1" />
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-background dark:bg-slate-900 transition-colors">
      {/* Header */}
      <div className="flex-none p-3 border-b bg-gradient-brand-light dark:border-slate-700">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-rezolve-green" />
            <span className="font-semibold text-rezolve-navy dark:text-slate-100">SNPod AI Assistant</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={toggleDarkMode}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className="text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white"
            >
              {isDark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handlePopOut}
              title="Open in new window"
              className="text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleSecondScreen}
              title="Open 2nd Screen (multi-panel view)"
              className="text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white"
            >
              <Monitor className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => refetch()}
              title="Refresh"
              className="text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        {context && (
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-rezolve-navy dark:text-white">{context.number}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(context.priority)}`}>
                {context.priority_display}
              </span>
            </div>
            <p className="text-xs text-muted-foreground dark:text-slate-400 line-clamp-2">
              {context.short_description}
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground dark:text-slate-400">
              <span>State: {context.state_display}</span>
              {context.caller?.name && <span>| {context.caller.name}</span>}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        <TabsList className="flex-none mx-3 mt-3 grid grid-cols-6 w-auto bg-rezolve-navy/10 dark:bg-slate-700 rounded-lg p-1">
          <TabsTrigger
            value="summary"
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-rezolve-green data-[state=active]:text-rezolve-green dark:data-[state=active]:text-white data-[state=active]:shadow-sm text-rezolve-navy/70 dark:text-slate-300 text-xs px-1 rounded-md"
          >
            <FileText className="h-3 w-3 mr-0.5" />
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="alerts"
            className="relative data-[state=active]:bg-white dark:data-[state=active]:bg-rezolve-green data-[state=active]:text-rezolve-green dark:data-[state=active]:text-white data-[state=active]:shadow-sm text-rezolve-navy/70 dark:text-slate-300 text-xs px-1 rounded-md"
          >
            <Bell className="h-3 w-3 mr-0.5" />
            Alerts
            {alertCount > 0 && (
              <span className="absolute -top-1 -right-1 px-1 min-w-[14px] h-[14px] text-[8px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center">
                {alertCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="chat"
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-rezolve-green data-[state=active]:text-rezolve-green dark:data-[state=active]:text-white data-[state=active]:shadow-sm text-rezolve-navy/70 dark:text-slate-300 text-xs px-1 rounded-md"
          >
            <Bot className="h-3 w-3 mr-0.5" />
            Ask Bot
          </TabsTrigger>
          <TabsTrigger
            value="analysis"
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-rezolve-green data-[state=active]:text-rezolve-green dark:data-[state=active]:text-white data-[state=active]:shadow-sm text-rezolve-navy/70 dark:text-slate-300 text-xs px-1 rounded-md"
          >
            <Sparkles className="h-3 w-3 mr-0.5" />
            Analysis
          </TabsTrigger>
          <TabsTrigger
            value="actions"
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-rezolve-green data-[state=active]:text-rezolve-green dark:data-[state=active]:text-white data-[state=active]:shadow-sm text-rezolve-navy/70 dark:text-slate-300 text-xs px-1 rounded-md"
          >
            <Play className="h-3 w-3 mr-0.5" />
            Actions
          </TabsTrigger>
          <TabsTrigger
            value="recording"
            className="data-[state=active]:bg-white dark:data-[state=active]:bg-rezolve-green data-[state=active]:text-rezolve-green dark:data-[state=active]:text-white data-[state=active]:shadow-sm text-rezolve-navy/70 dark:text-slate-300 text-xs px-1 rounded-md"
          >
            <Mic className="h-3 w-3 mr-0.5" />
            Recording
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto p-3 lite-scrollbar">
          <TabsContent value="alerts" className="mt-0 h-full">
            <AlertsTab
              multiUserData={analysis?.sentiment?.metrics?.multiUserEvent ? {
                isMultiUser: analysis.sentiment.metrics.multiUserEvent.isMultiUser,
                affectedScope: analysis.sentiment.metrics.multiUserEvent.affectedScope,
                score: analysis.sentiment.metrics.multiUserEvent.score,
                explanation: analysis.sentiment.metrics.multiUserEvent.explanation,
              } : null}
              vipData={analysis?.sentiment?.metrics?.vip ? {
                isVip: analysis.sentiment.metrics.vip.isVip,
                reason: analysis.sentiment.metrics.vip.reason,
                score: analysis.sentiment.metrics.vip.score,
                explanation: analysis.sentiment.metrics.vip.explanation,
              } : null}
              escalationData={analysis?.sentiment?.escalation || null}
              chronicData={analysis?.sentiment?.metrics?.chronic ? {
                score: analysis.sentiment.metrics.chronic.score,
                explanation: analysis.sentiment.metrics.chronic.explanation,
              } : null}
              salesOpportunityData={analysis?.salesOpportunity || null}
              onCreateSalesTicket={handleCreateSalesTicket}
              analysisLoading={analysisLoading}
              companyId={context?.company?.sys_id || null}
              companyName={context?.company?.name || null}
              ticketSummary={context?.short_description || null}
            />
          </TabsContent>

          <TabsContent value="summary" className="mt-0 h-full">
            <div className="space-y-3 max-w-lg mx-auto">
              {/* Row 1: Record Details, Caller, Company */}
              <div className="grid grid-cols-3 gap-2">
                {/* Record Details */}
                <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-2 shadow-sm">
                  <h3 className="font-medium text-rezolve-navy dark:text-white mb-1.5 text-[11px]">Record</h3>
                  {context && (
                    <dl className="space-y-1 text-[10px]">
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground dark:text-slate-400">Number</dt>
                        <dd className="font-medium truncate ml-1 dark:text-slate-200">{context.number}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground dark:text-slate-400">State</dt>
                        <dd className="truncate ml-1 dark:text-slate-200">{context.state_display}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground dark:text-slate-400">Priority</dt>
                        <dd className="truncate ml-1 dark:text-slate-200">{context.priority_display}</dd>
                      </div>
                      {context.category && (
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground dark:text-slate-400">Category</dt>
                          <dd className="truncate ml-1 dark:text-slate-200">{context.category}</dd>
                        </div>
                      )}
                    </dl>
                  )}
                </div>

                {/* Caller/Contact Info */}
                <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-2 shadow-sm">
                  <h3 className="font-medium text-rezolve-navy dark:text-white mb-1.5 text-[11px] flex items-center gap-1">
                    <User className="h-3 w-3" />
                    Caller
                  </h3>
                  {context?.caller ? (
                    <dl className="space-y-1 text-[10px]">
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground dark:text-slate-400">Name</dt>
                        <dd className="font-medium truncate ml-1 dark:text-slate-200">{context.caller.name}</dd>
                      </div>
                      {context.caller.email && (
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground dark:text-slate-400">Email</dt>
                          <dd className="truncate ml-1 max-w-[80px] dark:text-slate-200">{context.caller.email}</dd>
                        </div>
                      )}
                      {context.caller.title && (
                        <div className="flex justify-between">
                          <dt className="text-muted-foreground dark:text-slate-400">Title</dt>
                          <dd className="truncate ml-1 dark:text-slate-200">{context.caller.title}</dd>
                        </div>
                      )}
                    </dl>
                  ) : (
                    <p className="text-[10px] text-muted-foreground dark:text-slate-400">No caller info</p>
                  )}
                </div>

                {/* Company Info */}
                <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-2 shadow-sm">
                  <h3 className="font-medium text-rezolve-navy dark:text-white mb-1.5 text-[11px] flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    Company
                  </h3>
                  {context?.company ? (
                    <p className="text-[10px] font-medium dark:text-slate-200">{context.company.name}</p>
                  ) : (
                    <p className="text-[10px] text-muted-foreground dark:text-slate-400">No company info</p>
                  )}
                  {context?.assignment_group && (
                    <div className="mt-1.5 pt-1.5 border-t dark:border-slate-600">
                      <div className="text-[10px]">
                        <span className="text-muted-foreground dark:text-slate-400">Group: </span>
                        <span className="truncate dark:text-slate-200">{context.assignment_group.name}</span>
                      </div>
                    </div>
                  )}
                  {context?.assigned_to && (
                    <div className="text-[10px]">
                      <span className="text-muted-foreground dark:text-slate-400">Assigned: </span>
                      <span className="truncate dark:text-slate-200">{context.assigned_to.name}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Recent Activity */}
              {context?.journal_entries && context.journal_entries.length > 0 && (
                <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-3 shadow-sm">
                  <h3 className="font-medium text-rezolve-navy dark:text-white mb-2 text-sm flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    Recent Activity
                  </h3>
                  <div className="space-y-2">
                    {context.journal_entries.slice(0, 3).map((entry) => (
                      <div key={entry.sys_id} className="text-xs border-l-2 border-rezolve-green/30 pl-2">
                        <div className="flex items-center gap-1 text-muted-foreground dark:text-slate-400 mb-0.5">
                          <span className={entry.type === 'work_note' ? 'text-rezolve-green' : 'text-blue-500'}>
                            {entry.type === 'work_note' ? 'Work Note' : 'Comment'}
                          </span>
                          <span>by {entry.created_by}</span>
                        </div>
                        <p className="line-clamp-2 dark:text-slate-300">{entry.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Similar Records */}
              <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-2 shadow-sm">
                <div className="flex items-center justify-between mb-1.5">
                  <h3 className="font-medium text-rezolve-navy dark:text-white text-[11px] flex items-center gap-1">
                    <Copy className="h-3 w-3" />
                    Similar Records
                  </h3>
                  {similarRecords.length === 0 && !similarLoading && (
                    <button
                      onClick={handleGetSimilar}
                      disabled={!context}
                      className="text-[10px] text-rezolve-green hover:underline"
                    >
                      Find
                    </button>
                  )}
                </div>

                {similarLoading && (
                  <div className="flex items-center gap-2 py-2">
                    <Loader2 className="h-3 w-3 animate-spin text-rezolve-green" />
                    <span className="text-[10px] text-muted-foreground dark:text-slate-400">Finding...</span>
                  </div>
                )}

                {similarError && (
                  <p className="text-[10px] text-destructive">{similarError}</p>
                )}

                {!similarLoading && similarRecords.length === 0 && !similarError && (
                  <p className="text-[10px] text-muted-foreground dark:text-slate-400">Click "Find" to discover related tickets</p>
                )}

                {similarRecords.length > 0 && (
                  <div className="space-y-1.5">
                    {similarRecords.slice(0, 5).map((record) => (
                      <a
                        key={record.sys_id}
                        href={record.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-[10px] border-l-2 border-muted dark:border-slate-600 pl-1.5 hover:border-rezolve-green transition-colors"
                      >
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-rezolve-navy dark:text-rezolve-green">{record.number}</span>
                          <ExternalLink className="h-2 w-2 text-muted-foreground dark:text-slate-400" />
                        </div>
                        <p className="text-muted-foreground dark:text-slate-400 line-clamp-1">{record.short_description}</p>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Row 3: SLA Tracking + Attachments (2 columns) */}
              <div className="grid grid-cols-2 gap-2">
                {/* SLA Tracking */}
                <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-2 shadow-sm">
                  <h3 className="font-medium text-rezolve-navy dark:text-white text-[11px] flex items-center gap-1 mb-1.5">
                    <Timer className="h-3 w-3" />
                    SLA Status
                  </h3>

                  {slaLoading && (
                    <div className="flex items-center gap-2 py-2">
                      <Loader2 className="h-3 w-3 animate-spin text-rezolve-green" />
                      <span className="text-[10px] text-muted-foreground dark:text-slate-400">Loading...</span>
                    </div>
                  )}

                  {!slaLoading && slas.length === 0 && (
                    <p className="text-[10px] text-muted-foreground dark:text-slate-400">No active SLAs</p>
                  )}

                  {!slaLoading && slas.length > 0 && (
                    <div className="space-y-1.5">
                      {slas.slice(0, 3).map((sla) => (
                        <div
                          key={sla.sys_id}
                          className={`text-[10px] rounded border p-1.5 ${
                            sla.has_breached
                              ? 'bg-red-50 border-red-300'
                              : sla.percentage > 75
                              ? 'bg-amber-50 border-amber-300'
                              : 'bg-emerald-50 border-emerald-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium truncate">{sla.name}</span>
                            {sla.has_breached && (
                              <AlertTriangle className="h-3 w-3 text-red-500 flex-shrink-0" />
                            )}
                          </div>
                          <div className="flex items-center justify-between text-[9px]">
                            <span className={sla.has_breached ? 'text-red-600' : 'text-gray-500'}>
                              {sla.has_breached ? 'BREACHED' : sla.time_left}
                            </span>
                            <span className="text-gray-400">{Math.round(sla.percentage)}%</span>
                          </div>
                          {/* Progress bar */}
                          <div className="w-full h-1 bg-gray-200 rounded-full mt-1">
                            <div
                              className={`h-full rounded-full ${
                                sla.has_breached
                                  ? 'bg-red-500'
                                  : sla.percentage > 75
                                  ? 'bg-amber-500'
                                  : 'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.min(sla.percentage, 100)}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Attachments */}
                <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-2 shadow-sm">
                  <h3 className="font-medium text-rezolve-navy dark:text-white text-[11px] flex items-center gap-1 mb-1.5">
                    <Paperclip className="h-3 w-3" />
                    Attachments
                  </h3>

                  {attachmentsLoading && (
                    <div className="flex items-center gap-2 py-2">
                      <Loader2 className="h-3 w-3 animate-spin text-rezolve-green" />
                      <span className="text-[10px] text-muted-foreground dark:text-slate-400">Loading...</span>
                    </div>
                  )}

                  {!attachmentsLoading && attachments.length === 0 && (
                    <p className="text-[10px] text-muted-foreground dark:text-slate-400">No attachments</p>
                  )}

                  {!attachmentsLoading && attachments.length > 0 && (
                    <div className="space-y-1">
                      {attachments.slice(0, 4).map((att) => (
                        <a
                          key={att.sys_id}
                          href={att.download_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-[10px] p-1 rounded hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors group"
                        >
                          {att.content_type.startsWith('image/') ? (
                            <Image className="h-3 w-3 text-purple-500 flex-shrink-0" />
                          ) : (
                            <FileText className="h-3 w-3 text-blue-500 flex-shrink-0" />
                          )}
                          <span className="truncate flex-1 dark:text-slate-300 group-hover:text-rezolve-green">
                            {att.file_name}
                          </span>
                          <span className="text-[9px] text-gray-400 dark:text-slate-500 flex-shrink-0">
                            {att.size_display}
                          </span>
                          <Download className="h-2.5 w-2.5 text-gray-400 dark:text-slate-500 group-hover:text-rezolve-green flex-shrink-0" />
                        </a>
                      ))}
                      {attachments.length > 4 && (
                        <p className="text-[9px] text-muted-foreground dark:text-slate-400 text-center pt-1">
                          +{attachments.length - 4} more
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Row 4: Customer Entitlements */}
              {context?.company?.sys_id && (
                <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-2 shadow-sm">
                  <h3 className="font-medium text-rezolve-navy dark:text-white text-[11px] flex items-center gap-1 mb-1.5">
                    <Shield className="h-3 w-3 text-purple-500" />
                    Customer Entitlements
                  </h3>

                  {entitlementsLoading && (
                    <div className="flex items-center gap-2 py-2">
                      <Loader2 className="h-3 w-3 animate-spin text-rezolve-green" />
                      <span className="text-[10px] text-muted-foreground dark:text-slate-400">Loading entitlements...</span>
                    </div>
                  )}

                  {!entitlementsLoading && (!entitlements || entitlements.summary.total_entitlements === 0) && (
                    <p className="text-[10px] text-muted-foreground dark:text-slate-400">No active entitlements found</p>
                  )}

                  {!entitlementsLoading && entitlements && entitlements.summary.total_entitlements > 0 && (
                    <div className="space-y-2">
                      {/* Support Level Badge */}
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          entitlements.summary.support_level === 'Gold' || entitlements.summary.support_level === 'Premium'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                            : entitlements.summary.support_level === 'Silver'
                            ? 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'
                            : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                        }`}>
                          {entitlements.summary.support_level} Support
                        </span>
                        <span className="text-[9px] text-muted-foreground dark:text-slate-400">
                          {entitlements.summary.active_contracts} contract{entitlements.summary.active_contracts !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Entitlement Details */}
                      <div className="grid grid-cols-2 gap-1.5">
                        {entitlements.contracts.slice(0, 2).flatMap(c => c.entitlements.slice(0, 2)).map((ent) => (
                          <div
                            key={ent.sys_id}
                            className={`text-[9px] rounded border p-1.5 ${
                              ent.percentage_used > 90
                                ? 'bg-red-50 border-red-300 dark:bg-red-900/20 dark:border-red-700'
                                : ent.percentage_used > 70
                                ? 'bg-amber-50 border-amber-300 dark:bg-amber-900/20 dark:border-amber-700'
                                : 'bg-emerald-50 border-emerald-300 dark:bg-emerald-900/20 dark:border-emerald-700'
                            }`}
                          >
                            <div className="font-medium truncate dark:text-slate-200">{ent.name}</div>
                            <div className="flex items-center justify-between mt-0.5">
                              <span className="text-gray-500 dark:text-slate-400">
                                {ent.type === 'unlimited' ? 'Unlimited' : `${ent.remaining_units}/${ent.total_units} ${ent.type}`}
                              </span>
                              {ent.type !== 'unlimited' && (
                                <span className={`${
                                  ent.percentage_used > 90 ? 'text-red-600 dark:text-red-400' :
                                  ent.percentage_used > 70 ? 'text-amber-600 dark:text-amber-400' :
                                  'text-emerald-600 dark:text-emerald-400'
                                }`}>
                                  {100 - ent.percentage_used}% left
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="analysis" className="mt-0 h-full">
            {/* Loading State */}
            {analysisLoading && (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-rezolve-green mb-3" />
                <p className="text-sm text-muted-foreground transition-opacity duration-300">{THINKING_MESSAGES[thinkingMessageIndex]}</p>
                <p className="text-xs text-muted-foreground/60 mt-1">AI analysis typically takes 15-20 seconds</p>
              </div>
            )}

            {/* Error State */}
            {analysisError && !analysisLoading && (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <AlertCircle className="h-8 w-8 text-destructive mb-3" />
                <p className="text-sm text-destructive font-medium">{analysisError}</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={handleAnalyze}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
                </Button>
              </div>
            )}

            {/* Initial State */}
            {!analysis && !analysisLoading && !analysisError && (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <Sparkles className="h-12 w-12 text-rezolve-green/50 dark:text-rezolve-green/70 mb-3" />
                <h3 className="font-medium text-rezolve-navy dark:text-white mb-1">AI Analysis</h3>
                <p className="text-sm text-muted-foreground dark:text-slate-400 mb-4">Get AI-powered insights about this record</p>
                <Button onClick={handleAnalyze} disabled={!context} className="bg-rezolve-green hover:bg-rezolve-green/90">
                  <Sparkles className="h-4 w-4 mr-2" /> Analyze Record
                </Button>
              </div>
            )}

            {/* Analysis Results */}
            {analysis && !analysisLoading && (
              <div className="space-y-3">
                {/* AI Summary + Skills Row */}
                <div className="grid grid-cols-3 gap-2">
                  {/* AI Summary Card - 2 cols */}
                  <div className="col-span-2 bg-gradient-to-br from-rezolve-green/10 to-rezolve-navy/5 dark:from-slate-800 dark:to-slate-800 rounded-lg border border-rezolve-green/20 dark:border-slate-700 p-2 shadow-sm">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-rezolve-green" />
                      <h3 className="text-[10px] font-semibold text-rezolve-navy dark:text-white uppercase">AI Summary</h3>
                    </div>
                    <p className="text-[11px] text-gray-700 dark:text-slate-300 leading-relaxed line-clamp-4">{analysis.summary}</p>
                  </div>

                  {/* Skills Required - 1 col */}
                  <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-2 shadow-sm">
                    <div className="flex items-center gap-1 mb-1.5">
                      <Wrench className="h-3 w-3 text-blue-500" />
                      <span className="text-[10px] font-semibold text-rezolve-navy dark:text-white uppercase">Skills</span>
                    </div>
                    {analysis.sentiment?.metrics?.skills?.required && analysis.sentiment.metrics.skills.required.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {(analysis.sentiment.metrics.skills as SkillsMetricScore).required.slice(0, 4).map((skill, idx) => (
                          <span key={idx} className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700 rounded-full text-[9px] text-blue-700 dark:text-blue-300 font-medium">
                            {skill}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[10px] text-muted-foreground dark:text-slate-500">No specific skills</p>
                    )}
                  </div>
                </div>

                {/* Escalation, Sentiment & Actions Row (3 columns) */}
                <div className="grid grid-cols-3 gap-2">
                  {/* Escalation */}
                  <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between px-2 py-1 bg-rezolve-green">
                      <h3 className="text-[9px] font-semibold text-white uppercase">Escalation</h3>
                      <button onClick={handleAnalyze} className="text-white/70 hover:text-white">
                        <RefreshCw className="h-2.5 w-2.5" />
                      </button>
                    </div>
                    <div className="p-1.5">
                      <div className={`rounded border p-1.5 ${getEscalationColor(analysis.sentiment?.escalation?.score || 0)}`}>
                        <div className="flex items-center gap-1.5">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 ${
                            (analysis.sentiment?.escalation?.score || 0) >= 7 ? 'bg-red-500' :
                            (analysis.sentiment?.escalation?.score || 0) >= 4 ? 'bg-amber-500' : 'bg-emerald-500'
                          }`}>
                            {analysis.sentiment?.escalation?.score || 0}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-gray-800 dark:text-slate-200 font-semibold text-[9px] truncate">
                              {analysis.sentiment?.escalation?.recommended ? 'Recommended' : 'Not Needed'}
                            </div>
                          </div>
                        </div>
                      </div>
                      {analysis.sentiment?.escalation?.recommended && (
                        <Button className="w-full mt-1.5 bg-red-600 hover:bg-red-700 text-white h-5 text-[9px]" onClick={() => alert('Escalation feature coming soon!')}>
                          <ArrowUpCircle className="h-2.5 w-2.5 mr-0.5" /> Escalate
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Sentiment */}
                  <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 shadow-sm overflow-hidden">
                    <div className="px-2 py-1 bg-rezolve-green">
                      <h3 className="text-[9px] font-semibold text-white uppercase">Sentiment</h3>
                    </div>
                    <div className="p-1.5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          analysis.sentiment?.overall === 'negative' ? 'bg-red-500' :
                          analysis.sentiment?.overall === 'positive' ? 'bg-emerald-500' : 'bg-amber-500'
                        }`} />
                        <span className={`text-[10px] font-medium ${
                          analysis.sentiment?.overall === 'negative' ? 'text-red-600 dark:text-red-400' :
                          analysis.sentiment?.overall === 'positive' ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'
                        }`}>
                          {(analysis.sentiment?.overall || 'neutral').charAt(0).toUpperCase() + (analysis.sentiment?.overall || 'neutral').slice(1)}
                        </span>
                        <span className="text-gray-400 dark:text-slate-500 text-[9px]">{analysis.sentiment?.confidence || 0}%</span>
                      </div>
                      <p className="text-gray-600 dark:text-slate-400 text-[9px] leading-relaxed line-clamp-3">{analysis.summary}</p>
                    </div>
                  </div>

                  {/* Suggested Actions */}
                  <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 shadow-sm overflow-hidden">
                    <div className="px-2 py-1 bg-rezolve-green">
                      <h3 className="text-[9px] font-semibold text-white uppercase flex items-center gap-1">
                        <Lightbulb className="h-2.5 w-2.5" /> Actions
                      </h3>
                    </div>
                    <div className="p-1.5">
                      {analysis.suggestedActions && analysis.suggestedActions.length > 0 ? (
                        <ul className="space-y-0.5">
                          {analysis.suggestedActions.slice(0, 3).map((action, i) => (
                            <li key={i} className="text-[9px] text-gray-600 dark:text-slate-400 flex items-start gap-1">
                              <span className="text-rezolve-green font-bold">{i + 1}.</span>
                              <span className="line-clamp-2">{action}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-[9px] text-muted-foreground dark:text-slate-500">No actions suggested</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* VIP Badge */}
                {analysis.sentiment?.metrics?.vip?.isVip && (
                  <div className="bg-gradient-to-r from-amber-100 to-yellow-100 border border-amber-400 rounded-lg p-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center">
                        <Crown className="h-3.5 w-3.5 text-white" />
                      </div>
                      <div>
                        <div className="text-amber-700 font-semibold text-xs">VIP / Executive Contact</div>
                        <div className="text-amber-600 text-[10px]">{analysis.sentiment.metrics.vip.reason}</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Ticket Health Metrics Grid */}
                <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between px-2 py-1.5 bg-rezolve-green">
                    <h3 className="text-[10px] font-semibold text-white uppercase">Ticket Health Metrics</h3>
                    <button onClick={() => setSelectedMetric(null)} className="text-[9px] text-white/70 hover:text-white">
                      {selectedMetric ? 'Hide details' : 'Click for details'}
                    </button>
                  </div>
                  <div className="p-2">
                    <div className="grid grid-cols-4 gap-1">
                      {METRICS.map((metric) => {
                        const metricData = analysis.sentiment?.metrics?.[metric.key] as MetricScore | undefined
                        const score = metricData?.score || 0
                        const explanation = metricData?.explanation || ''

                        // Special rendering for Multi-User Event
                        if (metric.key === 'multiUserEvent') {
                          const muData = analysis.sentiment?.metrics?.multiUserEvent as MultiUserEventMetricScore | undefined
                          const isMultiUser = muData?.isMultiUser || (muData?.score && muData.score >= 5)
                          const muExplanation = muData?.explanation || (isMultiUser ? muData?.affectedScope : 'No multi-user event detected')
                          return (
                            <button
                              key={metric.key}
                              onClick={() => setSelectedMetric(selectedMetric === metric.key ? null : metric.key)}
                              title={muExplanation}
                              className={`rounded border p-1 text-center transition-all cursor-pointer hover:scale-105 ${
                                isMultiUser
                                  ? 'bg-violet-100 dark:bg-violet-900/60 border-violet-400 dark:border-violet-500'
                                  : 'bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600'
                              } ${selectedMetric === metric.key ? 'ring-2 ring-rezolve-green ring-offset-1 dark:ring-offset-slate-800' : ''}`}
                            >
                              <div className="text-[9px] font-bold text-gray-600 dark:text-slate-300 mb-0.5">{metric.label}</div>
                              <div className={`text-sm font-bold ${isMultiUser ? 'text-violet-600 dark:text-violet-400' : 'text-gray-400 dark:text-slate-500'}`}>
                                {isMultiUser ? <Users className="h-3.5 w-3.5 mx-auto" /> : '—'}
                              </div>
                              <div className="text-[8px] text-gray-500 dark:text-slate-400">{isMultiUser ? 'Yes' : 'No'}</div>
                            </button>
                          )
                        }

                        return (
                          <button
                            key={metric.key}
                            onClick={() => setSelectedMetric(selectedMetric === metric.key ? null : metric.key)}
                            title={explanation || `${metric.label}: Score ${score}/10`}
                            className={`rounded border p-1 text-center transition-all cursor-pointer hover:scale-105 ${getScoreColor(score, metric.invertColor, isDark)} ${
                              selectedMetric === metric.key ? 'ring-2 ring-rezolve-green ring-offset-1 dark:ring-offset-slate-800' : ''
                            }`}
                          >
                            <div className="text-[9px] font-bold text-gray-600 dark:text-slate-300 mb-0.5">{metric.label}</div>
                            <div className={`text-lg font-bold ${getScoreTextColor(score, metric.invertColor, isDark)}`}>{score}</div>
                            <div className="text-[8px] text-gray-500 dark:text-slate-400">/ 10</div>
                          </button>
                        )
                      })}
                    </div>

                    {/* Selected Metric Explanation */}
                    {selectedMetric && analysis.sentiment?.metrics?.[selectedMetric] && (
                      <div className="mt-2 p-2 bg-gray-50 dark:bg-slate-700 rounded border border-gray-200 dark:border-slate-600 animate-in fade-in slide-in-from-top-1 duration-200">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Info className="h-3 w-3 text-rezolve-green" />
                          <span className="text-[10px] font-semibold text-rezolve-green uppercase">
                            {METRICS.find(m => m.key === selectedMetric)?.label} Explanation
                          </span>
                        </div>
                        <p className="text-[10px] text-gray-600 dark:text-slate-300 leading-relaxed">
                          {(analysis.sentiment.metrics[selectedMetric] as MetricScore).explanation}
                        </p>
                        {selectedMetric === 'skills' && (analysis.sentiment.metrics.skills as SkillsMetricScore)?.required?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-slate-600">
                            <div className="text-[10px] text-gray-500 dark:text-slate-400 mb-1">Required Skills:</div>
                            <div className="flex flex-wrap gap-1">
                              {(analysis.sentiment.metrics.skills as SkillsMetricScore).required.map((skill, idx) => (
                                <span key={idx} className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/50 border border-blue-300 dark:border-blue-600 rounded text-[9px] text-blue-700 dark:text-blue-300">{skill}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {selectedMetric === 'multiUserEvent' && (analysis.sentiment.metrics.multiUserEvent as MultiUserEventMetricScore)?.affectedScope && (
                          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-slate-600">
                            <div className="text-[10px] text-gray-500 dark:text-slate-400 mb-1">Affected Scope:</div>
                            <div className="text-[10px] text-violet-600 dark:text-violet-400">{(analysis.sentiment.metrics.multiUserEvent as MultiUserEventMetricScore).affectedScope}</div>
                          </div>
                        )}
                        {selectedMetric === 'responseQuality' && (
                          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-slate-600 space-y-2">
                            {(analysis.sentiment.metrics.responseQuality as ResponseQualityMetricScore)?.strengths?.length > 0 && (
                              <div>
                                <div className="flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium mb-1">
                                  <ThumbsUp className="h-3 w-3" /> Strengths
                                </div>
                                <ul className="space-y-0.5">
                                  {(analysis.sentiment.metrics.responseQuality as ResponseQualityMetricScore).strengths.map((s, i) => (
                                    <li key={i} className="text-[10px] text-gray-600 dark:text-slate-300 flex items-start gap-1"><span className="text-emerald-400">+</span>{s}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {(analysis.sentiment.metrics.responseQuality as ResponseQualityMetricScore)?.improvements?.length > 0 && (
                              <div>
                                <div className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400 font-medium mb-1">
                                  <ThumbsDown className="h-3 w-3" /> Improvements
                                </div>
                                <ul className="space-y-0.5">
                                  {(analysis.sentiment.metrics.responseQuality as ResponseQualityMetricScore).improvements.map((s, i) => (
                                    <li key={i} className="text-[10px] text-gray-600 dark:text-slate-300 flex items-start gap-1"><span className="text-amber-400">-</span>{s}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Meta info */}
                {analysis._meta && (
                  <p className="text-[10px] text-center text-muted-foreground/50">
                    {analysis._meta.tokens} tokens • {analysis._meta.responseTimeMs}ms
                  </p>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="actions" className="mt-0 h-full">
            <div className="space-y-2 max-w-lg mx-auto">
              {/* AI Response Generator - Top */}
              <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-2 py-1.5 bg-gradient-to-r from-rezolve-green/10 to-blue-500/10 dark:from-slate-700/50 dark:to-slate-700/50 border-b dark:border-slate-600">
                  <div className="flex items-center gap-1.5">
                    <Wand2 className="h-3.5 w-3.5 text-rezolve-green" />
                    <span className="font-medium text-rezolve-navy dark:text-white text-xs">AI Response</span>
                  </div>
                  <div className="flex gap-0.5">
                    <button onClick={() => setResponseTone('professional')}
                      className={`text-[8px] px-1.5 py-0.5 rounded-l transition-colors ${responseTone === 'professional' ? 'bg-rezolve-green text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-slate-400'}`}>
                      Pro
                    </button>
                    <button onClick={() => setResponseTone('friendly')}
                      className={`text-[8px] px-1.5 py-0.5 transition-colors ${responseTone === 'friendly' ? 'bg-pink-500 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-slate-400'}`}>
                      Friendly
                    </button>
                    <button onClick={() => setResponseTone('technical')}
                      className={`text-[8px] px-1.5 py-0.5 rounded-r transition-colors ${responseTone === 'technical' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-slate-400'}`}>
                      Tech
                    </button>
                  </div>
                </div>
                <div className="p-2">
                  <div className="flex gap-1.5">
                    <Button onClick={handleGenerateResponse} disabled={responseLoading || !context}
                      className="flex-1 bg-rezolve-green hover:bg-rezolve-green/90 h-7 text-[10px]">
                      {responseLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Wand2 className="h-3 w-3 mr-1" />Generate Response</>}
                    </Button>
                    <button onClick={() => setResponseLength(responseLength === 'concise' ? 'detailed' : 'concise')}
                      className="text-[8px] px-2 py-1 rounded border dark:border-slate-600 bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-slate-300">
                      {responseLength === 'concise' ? 'Short' : 'Long'}
                    </button>
                  </div>
                  {responseError && <p className="text-[9px] text-red-500 mt-1">{responseError}</p>}

                  {/* Generated Response with Add to Note */}
                  {generatedResponse && (
                    <div className="mt-2 bg-gray-50 dark:bg-slate-700/50 rounded-lg border dark:border-slate-600 p-2">
                      <div className="bg-white dark:bg-slate-800 rounded border dark:border-slate-600 p-2 max-h-48 overflow-y-auto mb-2">
                        <p className="text-[11px] text-gray-700 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">{generatedResponse}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button onClick={() => { setNoteInput(generatedResponse); clearGeneratedResponse(); }}
                          className="flex-1 h-6 text-[9px] bg-blue-500 hover:bg-blue-600">
                          <ArrowUpCircle className="h-3 w-3 mr-1" />Add to Note
                        </Button>
                        <Button onClick={handleCopyResponse} variant="outline" size="sm" className="h-6 text-[9px] px-2">
                          {copySuccess ? <CheckCircle className="h-3 w-3 text-green-500" /> : <><Copy className="h-3 w-3 mr-1" />Copy</>}
                        </Button>
                        <Button onClick={clearGeneratedResponse} variant="ghost" size="sm" className="h-6 w-6 p-0">
                          <Trash2 className="h-3 w-3 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Add Note + Time */}
              <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-2 py-1.5 bg-gray-50 dark:bg-slate-700/50 border-b dark:border-slate-600">
                  <div className="flex items-center gap-2">
                    <PenLine className="h-3.5 w-3.5 text-blue-500" />
                    <div className="flex">
                      <button onClick={() => setNoteType('work_note')}
                        className={`text-[9px] px-2 py-0.5 rounded-l transition-colors ${noteType === 'work_note' ? 'bg-rezolve-green text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-slate-300'}`}>
                        Internal
                      </button>
                      <button onClick={() => setNoteType('comment')}
                        className={`text-[9px] px-2 py-0.5 rounded-r transition-colors ${noteType === 'comment' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-slate-300'}`}>
                        Customer
                      </button>
                    </div>
                  </div>
                </div>
                <div className="p-2 space-y-2">
                  <textarea
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    placeholder={noteType === 'work_note' ? 'Internal work note...' : 'Customer-visible comment...'}
                    className="w-full text-[11px] border dark:border-slate-600 rounded-lg px-2.5 py-2 resize-none bg-white dark:bg-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-rezolve-green/50 placeholder-gray-400 dark:placeholder-slate-500"
                    rows={3}
                  />

                  {/* Compact Time Row */}
                  <div className="flex items-center gap-2 text-[9px]">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={timeHours > 0 || timeMinutes > 0}
                        onChange={(e) => {
                          if (!e.target.checked) { setTimeHours(0); setTimeMinutes(0); }
                          else {
                            const sugH = Math.floor(suggestedTime / 60);
                            const sugM = suggestedTime % 60;
                            setTimeHours(sugH); setTimeMinutes(sugM);
                          }
                        }}
                        className="w-3 h-3 rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                      <Clock className="h-3 w-3 text-purple-500" />
                    </label>
                    {(timeHours > 0 || timeMinutes > 0) ? (
                      <>
                        <div className="flex gap-0.5">
                          {[15, 30, 45, 60].map((mins) => (
                            <button key={mins}
                              onClick={() => { if (mins === 60) { setTimeHours(1); setTimeMinutes(0); } else { setTimeHours(0); setTimeMinutes(mins); } }}
                              className={`px-1.5 py-0.5 rounded transition-colors ${
                                (mins === 60 && timeHours === 1 && timeMinutes === 0) || (mins !== 60 && timeHours === 0 && timeMinutes === mins)
                                  ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                              }`}>
                              {mins === 60 ? '1h' : `${mins}m`}
                            </button>
                          ))}
                          {suggestedTime > 0 && suggestedTime !== 15 && suggestedTime !== 30 && suggestedTime !== 45 && suggestedTime !== 60 && (
                            <button
                              onClick={() => { setTimeHours(Math.floor(suggestedTime / 60)); setTimeMinutes(suggestedTime % 60); }}
                              className={`px-1.5 py-0.5 rounded transition-colors border border-purple-400 ${
                                (timeHours * 60 + timeMinutes) === suggestedTime
                                  ? 'bg-purple-600 text-white' : 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                              }`}
                              title="Suggested based on time on screen">
                              ~{suggestedTime}m
                            </button>
                          )}
                        </div>
                        <div className="flex items-center gap-0.5">
                          <input type="number" min="0" max="8" value={timeHours} onChange={(e) => setTimeHours(parseInt(e.target.value) || 0)}
                            className="w-8 text-center border dark:border-slate-600 rounded px-0.5 py-0.5 bg-white dark:bg-slate-700 dark:text-slate-200" />
                          <span className="text-purple-500 font-bold">:</span>
                          <input type="number" min="0" max="59" step="15" value={timeMinutes} onChange={(e) => setTimeMinutes(parseInt(e.target.value) || 0)}
                            className="w-8 text-center border dark:border-slate-600 rounded px-0.5 py-0.5 bg-white dark:bg-slate-700 dark:text-slate-200" />
                        </div>
                        <label className="flex items-center gap-0.5 cursor-pointer ml-auto">
                          <input type="checkbox" checked={timeBillable} onChange={(e) => setTimeBillable(e.target.checked)}
                            className="w-2.5 h-2.5 rounded border-gray-300 text-purple-600" />
                          <span className="text-purple-600 dark:text-purple-400">$</span>
                        </label>
                      </>
                    ) : (
                      <span className="text-gray-400 dark:text-slate-500">Add time ({suggestedTime > 0 ? `~${suggestedTime}m suggested` : 'optional'})</span>
                    )}
                  </div>

                  {/* Submit */}
                  <div className="flex items-center justify-between">
                    <div className="text-[9px]">
                      {(noteSuccess || commentSuccess) && <span className="text-green-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" />Added!</span>}
                      {(noteError || commentError) && <span className="text-red-500">{noteError || commentError}</span>}
                      {timeSuccess && <span className="text-purple-600 flex items-center gap-1"><Clock className="h-3 w-3" />Logged!</span>}
                      {timeError && <span className="text-red-500">{timeError}</span>}
                      {!noteSuccess && !commentSuccess && !noteError && !commentError && !timeSuccess && !timeError && (
                        <span className="text-gray-400 dark:text-slate-500">
                          {noteType === 'work_note' ? 'Internal' : 'Customer'}
                          {(timeHours > 0 || timeMinutes > 0) && ` + ${timeHours}h${timeMinutes}m`}
                        </span>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        if (noteInput.trim()) handleSubmitNote();
                        if (timeHours > 0 || timeMinutes > 0) handleSubmitTimeEntry();
                      }}
                      disabled={noteLoading || commentLoading || timeLoading || (!noteInput.trim() && timeHours === 0 && timeMinutes === 0)}
                      className={`h-7 text-[10px] px-3 ${noteType === 'work_note' ? 'bg-rezolve-green hover:bg-rezolve-green/90' : 'bg-blue-500 hover:bg-blue-600'}`}
                    >
                      {(noteLoading || commentLoading || timeLoading) ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Send className="h-3 w-3 mr-1" />Submit</>}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Quick Actions - Bottom */}
              {context && (
                <div className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700 p-2 shadow-sm space-y-2">
                  {/* Toast for escalation */}
                  {showEscalationToast && (
                    <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg px-3 py-2 text-xs text-amber-700 dark:text-amber-300 text-center">
                      Feature coming soon
                    </div>
                  )}
                  {/* Success message for documentation */}
                  {docSuccess && (
                    <div className="bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 rounded-lg px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Documentation ticket {docSuccess} created
                      </div>
                      {docItGlueUrl && (
                        <a href={docItGlueUrl} target="_blank" rel="noopener noreferrer"
                          className="flex items-center justify-center gap-1 mt-1 text-emerald-600 dark:text-emerald-400 hover:underline">
                          <ExternalLink className="h-3 w-3" />
                          Open IT Glue doc (draft)
                        </a>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Play className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                    {context.state !== '6' && context.state !== '7' ? (
                      <>
                        <Button onClick={() => handleQuickAction('resolve')} disabled={actionLoading !== null}
                          className="flex-1 h-6 text-[9px] bg-emerald-600 hover:bg-emerald-700">
                          {actionLoading === 'resolve' ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CheckCircle className="h-3 w-3 mr-0.5" />Resolve</>}
                        </Button>
                        <Button onClick={() => handleQuickAction('close')} disabled={actionLoading !== null}
                          className="flex-1 h-6 text-[9px] bg-slate-600 hover:bg-slate-700">
                          {actionLoading === 'close' ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Square className="h-3 w-3 mr-0.5" />Close</>}
                        </Button>
                      </>
                    ) : (
                      <Button onClick={() => handleQuickAction('reopen')} disabled={actionLoading !== null}
                        className="flex-1 h-6 text-[9px] bg-amber-600 hover:bg-amber-700">
                        {actionLoading === 'reopen' ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RefreshCw className="h-3 w-3 mr-0.5" />Reopen</>}
                      </Button>
                    )}
                  </div>
                  {/* Documentation and Escalation row */}
                  <div className="flex items-center gap-2">
                    <Wrench className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
                    <Button onClick={handleOpenDocModal} disabled={docLoading}
                      className="flex-1 h-6 text-[9px] bg-blue-600 hover:bg-blue-700">
                      {docLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <><FilePlus className="h-3 w-3 mr-0.5" />Documentation</>}
                    </Button>
                    <Button onClick={handleEscalation}
                      className="flex-1 h-6 text-[9px] bg-orange-500 hover:bg-orange-600">
                      <ArrowUpCircle className="h-3 w-3 mr-0.5" />Escalation
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="chat" className="mt-0 h-full flex flex-col max-w-lg mx-auto">
            {/* Chat Messages Area */}
            <div className="flex-1 overflow-y-auto space-y-3 mb-3 lite-scrollbar">
              {messages.length === 0 && !chatLoading && (
                <div className="flex flex-col items-center justify-center h-full text-center py-6">
                  <Bot className="h-10 w-10 text-rezolve-green/50 dark:text-rezolve-green/70 mb-3" />
                  <h3 className="font-medium text-rezolve-navy dark:text-white mb-1 text-sm">Ask Bot</h3>
                  <p className="text-xs text-muted-foreground dark:text-slate-400 px-4 mb-3">
                    Ask me anything about this ticket - resolution steps, documentation, similar issues, and more.
                  </p>
                  <div className="grid grid-cols-3 gap-1.5 w-full px-2">
                    <button
                      onClick={() => setChatInput('What is the quickest way to resolve this issue?')}
                      className="text-left text-[9px] bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg px-2 py-1.5 hover:border-rezolve-green/50 hover:bg-rezolve-green/5 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                    >
                      <Lightbulb className="h-3 w-3 text-rezolve-green flex-shrink-0" />
                      <span className="dark:text-slate-200 truncate">Quick fix?</span>
                    </button>
                    <button
                      onClick={() => setChatInput('Find documentation or knowledge base articles for this issue')}
                      className="text-left text-[9px] bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg px-2 py-1.5 hover:border-blue-500/50 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                    >
                      <BookOpen className="h-3 w-3 text-blue-500 flex-shrink-0" />
                      <span className="dark:text-slate-200 truncate">Find KB</span>
                    </button>
                    <button
                      onClick={() => setChatInput('Find similar tickets and how they were resolved')}
                      className="text-left text-[9px] bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg px-2 py-1.5 hover:border-purple-500/50 hover:bg-purple-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                    >
                      <Copy className="h-3 w-3 text-purple-500 flex-shrink-0" />
                      <span className="dark:text-slate-200 truncate">Similar</span>
                    </button>
                    <button
                      onClick={() => setChatInput('What are the possible root causes of this issue?')}
                      className="text-left text-[9px] bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg px-2 py-1.5 hover:border-amber-500/50 hover:bg-amber-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                    >
                      <Search className="h-3 w-3 text-amber-500 flex-shrink-0" />
                      <span className="dark:text-slate-200 truncate">Root cause?</span>
                    </button>
                    <button
                      onClick={() => setChatInput('Summarize all the activity and communications on this ticket')}
                      className="text-left text-[9px] bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg px-2 py-1.5 hover:border-cyan-500/50 hover:bg-cyan-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                    >
                      <FileText className="h-3 w-3 text-cyan-500 flex-shrink-0" />
                      <span className="dark:text-slate-200 truncate">Summarize</span>
                    </button>
                    <button
                      onClick={() => setChatInput('Find resources or individuals who can help escalate this ticket')}
                      className="text-left text-[9px] bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg px-2 py-1.5 hover:border-orange-500/50 hover:bg-orange-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                    >
                      <UserPlus className="h-3 w-3 text-orange-500 flex-shrink-0" />
                      <span className="dark:text-slate-200 truncate">Escalation</span>
                    </button>
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-xs ${
                      msg.role === 'user'
                        ? 'bg-rezolve-green text-white'
                        : 'bg-white dark:bg-slate-800 border dark:border-slate-700 shadow-sm text-gray-700 dark:text-slate-200'
                    }`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-1 mb-1 text-rezolve-navy dark:text-rezolve-green font-medium">
                        <Bot className="h-3 w-3" />
                        AI
                      </div>
                    )}
                    {msg.role === 'assistant' ? (
                      <div className="text-xs leading-relaxed prose prose-sm dark:prose-invert max-w-none prose-a:text-rezolve-green prose-a:font-semibold prose-a:no-underline hover:prose-a:underline prose-p:my-1 prose-ul:my-1 prose-li:my-0">
                        <ReactMarkdown
                          components={{
                            a: ({ href, children }) => (
                              <a href={href} target="_blank" rel="noopener noreferrer" className="text-rezolve-green font-semibold hover:underline">
                                {children}
                              </a>
                            ),
                            p: ({ children }) => <p className="my-1">{children}</p>,
                            ul: ({ children }) => <ul className="my-1 ml-4 list-disc">{children}</ul>,
                            li: ({ children }) => <li className="my-0">{children}</li>,
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap text-xs">{msg.content}</div>
                    )}
                  </div>
                </div>
              ))}

              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-white dark:bg-slate-800 border dark:border-slate-700 shadow-sm rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground dark:text-slate-400">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      <span className="transition-opacity duration-300">{THINKING_MESSAGES[thinkingMessageIndex]}</span>
                    </div>
                  </div>
                </div>
              )}

              {chatError && (
                <div className="flex justify-center">
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-600">
                    {chatError}
                  </div>
                </div>
              )}

              <div ref={chatEndRef} />
            </div>

            {/* Suggested Questions - show after bot responds */}
            {messages.length > 0 && !chatLoading && messages[messages.length - 1]?.role === 'assistant' && (
              <div className="flex-none py-2 border-t dark:border-slate-700">
                <div className="grid grid-cols-3 gap-1.5 w-full">
                  <button
                    onClick={() => setChatInput('What is the quickest way to resolve this issue?')}
                    className="text-left text-[9px] bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg px-2 py-1.5 hover:border-rezolve-green/50 hover:bg-rezolve-green/5 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                  >
                    <Lightbulb className="h-3 w-3 text-rezolve-green flex-shrink-0" />
                    <span className="dark:text-slate-200 truncate">Quick fix?</span>
                  </button>
                  <button
                    onClick={() => setChatInput('Find documentation or knowledge base articles for this issue')}
                    className="text-left text-[9px] bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg px-2 py-1.5 hover:border-blue-500/50 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                  >
                    <BookOpen className="h-3 w-3 text-blue-500 flex-shrink-0" />
                    <span className="dark:text-slate-200 truncate">Find KB</span>
                  </button>
                  <button
                    onClick={() => setChatInput('Find similar tickets and how they were resolved')}
                    className="text-left text-[9px] bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg px-2 py-1.5 hover:border-purple-500/50 hover:bg-purple-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                  >
                    <Copy className="h-3 w-3 text-purple-500 flex-shrink-0" />
                    <span className="dark:text-slate-200 truncate">Similar</span>
                  </button>
                  <button
                    onClick={() => setChatInput('What are the possible root causes of this issue?')}
                    className="text-left text-[9px] bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg px-2 py-1.5 hover:border-amber-500/50 hover:bg-amber-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                  >
                    <Search className="h-3 w-3 text-amber-500 flex-shrink-0" />
                    <span className="dark:text-slate-200 truncate">Root cause?</span>
                  </button>
                  <button
                    onClick={() => setChatInput('Summarize all the activity and communications on this ticket')}
                    className="text-left text-[9px] bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg px-2 py-1.5 hover:border-cyan-500/50 hover:bg-cyan-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                  >
                    <FileText className="h-3 w-3 text-cyan-500 flex-shrink-0" />
                    <span className="dark:text-slate-200 truncate">Summarize</span>
                  </button>
                  <button
                    onClick={() => setChatInput('Find resources or individuals who can help escalate this ticket')}
                    className="text-left text-[9px] bg-white dark:bg-slate-800 border dark:border-slate-600 rounded-lg px-2 py-1.5 hover:border-orange-500/50 hover:bg-orange-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-1"
                  >
                    <UserPlus className="h-3 w-3 text-orange-500 flex-shrink-0" />
                    <span className="dark:text-slate-200 truncate">Escalation</span>
                  </button>
                </div>
              </div>
            )}

            {/* Chat Input */}
            <div className="flex-none border-t dark:border-slate-700 pt-3">
              {messages.length > 0 && (
                <div className="flex justify-end mb-2">
                  <button
                    onClick={clearChat}
                    className="text-xs text-muted-foreground hover:text-destructive flex items-center gap-1"
                  >
                    <Trash2 className="h-3 w-3" />
                    Clear chat
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <textarea
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Ask about this ticket..."
                  className="flex-1 text-xs border dark:border-slate-600 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-rezolve-green/50 bg-white dark:bg-slate-800 dark:text-slate-200 dark:placeholder-slate-400"
                  rows={2}
                  disabled={chatLoading || !context}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={chatLoading || !chatInput.trim() || !context}
                  size="sm"
                  className="bg-rezolve-green hover:bg-rezolve-green/90 self-end"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="recording" className="mt-0 h-full">
            <RecordingPanel
              sysId={context?.sys_id || null}
              table={context?.table || null}
              recordDescription={context ? `${context.number}: ${context.short_description}` : undefined}
            />
          </TabsContent>
        </div>
      </Tabs>

      {/* Footer */}
      <div className="flex-none p-2 border-t dark:border-slate-700 bg-muted/30 dark:bg-slate-800/50">
        <a href="https://xop.ai" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 hover:opacity-80 transition-opacity">
          <span className="text-xs text-muted-foreground dark:text-slate-400">Powered by</span>
          <img src="/xop-logo.png" alt="xop.ai" className="h-8 w-auto" />
        </a>
      </div>

      {/* Documentation Ticket Modal */}
      {showDocModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b dark:border-slate-700 bg-blue-50 dark:bg-slate-700/50">
              <div className="flex items-center gap-2">
                <FilePlus className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <h3 className="font-medium text-sm text-blue-900 dark:text-white">Create Documentation Ticket</h3>
              </div>
              <button
                onClick={() => setShowDocModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={docTitle}
                  onChange={(e) => setDocTitle(e.target.value)}
                  className="w-full text-sm border dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  placeholder="Documentation ticket title..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Description
                </label>
                <textarea
                  value={docDescription}
                  onChange={(e) => setDocDescription(e.target.value)}
                  className="w-full text-sm border dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none"
                  rows={8}
                  placeholder="Describe what documentation is needed..."
                />
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3 text-xs text-gray-600 dark:text-slate-400">
                <p className="font-medium mb-1">This will create:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>ServiceNow {context?.table === 'incident' ? 'Incident' : context?.table === 'problem' ? 'Problem' : 'Request'} (Documentation category)</li>
                  <li>Linked to: {context?.number}</li>
                  {messages.length > 0 && (
                    <li className="text-blue-600 dark:text-blue-400">AI-generated documentation from chat ({messages.length} messages)</li>
                  )}
                  <li className="text-purple-600 dark:text-purple-400">IT Glue document (if configured)</li>
                </ul>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50">
              <Button
                onClick={() => setShowDocModal(false)}
                variant="outline"
                size="sm"
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitDocumentation}
                disabled={docLoading || !docTitle.trim()}
                size="sm"
                className="text-xs bg-blue-600 hover:bg-blue-700"
              >
                {docLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FilePlus className="h-3 w-3 mr-1" />}
                Create Ticket
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
